import { useEffect } from "react";

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSEO({ title, description, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("description", description);
    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => {
      if (script) document.head.removeChild(script);
      document.title = "FIRST2JOB — Emprego em Portugal";
    };
  }, [title, description, jsonLd]);
}

function escapeHtml(t) {
  return String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Google for Jobs aceita HTML básico na descrição (<p>, <br>, <ul>, <li>, <strong>)
function descriptionHtml(job) {
  const blocks = [job.description, job.requirements ? `Requisitos:\n${job.requirements}` : ""].filter(Boolean).join("\n\n");
  return blocks
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br>")}</p>`)
    .join("");
}

const EMPLOYMENT = { "Full-time": "FULL_TIME", "Part-time": "PART_TIME", "Estágio": "INTERN", "Temporário": "TEMPORARY" };

/**
 * JSON-LD JobPosting completo (Google for Jobs).
 * https://developers.google.com/search/docs/appearance/structured-data/job-posting
 * Não deve ser emitido para vagas expiradas (o Google exige remover o markup).
 */
export function jobPostingJsonLd(job) {
  if (!job || job.expired || job.status !== "active") return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const internal = !job.source_type || job.source_type === "internal";
  const ld = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: descriptionHtml(job),
    identifier: { "@type": "PropertyValue", name: internal ? "FIRST2JOB" : (job.source_label || job.source_type), value: job.id },
    datePosted: job.created_at,
    employmentType: EMPLOYMENT[job.regime] || "FULL_TIME",
    hiringOrganization: {
      "@type": "Organization",
      name: job.company_name || "Empresa confidencial",
      ...(job.company_slug ? { sameAs: job.company_website || `${origin}/empresas/${job.company_slug}` } : job.company_website ? { sameAs: job.company_website } : {}),
      ...(job.company_logo_url ? { logo: `${origin}${job.company_logo_url}` } : {}),
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        ...(job.concelho ? { addressLocality: job.concelho } : {}),
        ...(job.district ? { addressRegion: job.district } : {}),
        addressCountry: "PT",
      },
    },
    industry: job.category,
    directApply: internal,
    url: `${origin}/empregos/${job.slug}`,
  };
  if (job.valid_through) ld.validThrough = job.valid_through;
  if (job.work_mode === "Remoto") {
    ld.jobLocationType = "TELECOMMUTE";
    ld.applicantLocationRequirements = { "@type": "Country", name: "Portugal" };
  }
  if (job.salary_min || job.salary_max) {
    ld.baseSalary = {
      "@type": "MonetaryAmount",
      currency: "EUR",
      value: {
        "@type": "QuantitativeValue",
        minValue: job.salary_min || job.salary_max,
        maxValue: job.salary_max || job.salary_min,
        unitText: "MONTH",
      },
    };
  }
  return ld;
}
